import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useGetAdvanceScreenContent from 'features/advance/hooks/useGetAdvanceScreenContent';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import {
  ONBOARDING,
  PROGRESS_TRACKER_FUNDING,
} from 'shared/constants/onboarding';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import useVaroAdvanceFlow from 'shared/utils/useVaroAdvanceFlow/useVaroAdvanceFlow';

const Pending = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'pending',
  });

  const doneLabel = translatedText('buttonDone');

  // Fetch screen content from GQL
  const {
    result: advanceScreenContent,
    loading: advanceScreenContentLoading,
    errorModel: advanceScreenContentErrorModel,
  } = useGetAdvanceScreenContent();

  const defaultError = useDefaultError();

  const pendingScreenContent = advanceScreenContent?.pendingScreenContent;

  const dismissToTab = useDismissToRootTab();

  const flow = useVaroAdvanceFlow();

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.LINKED_ACCOUNTS_TILE,
    HomeTileTypeIds.EXTERNAL_ACCOUNTS_TILE,
    HomeTileTypeIds.LIST_ITEM_MANAGE_EXTERNAL_ACCOUNTS,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
  ]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  const goHome = () => {
    if (flow === PROGRESS_TRACKER_FUNDING) {
      router.replace('/progress-tracker-funding');
    } else if (flow === ONBOARDING) {
      router.replace('/onboarding');
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={
          advanceScreenContentLoading && !advanceScreenContentErrorModel
        }
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          advanceScreenContentErrorModel ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultError.title}
              messageBody={defaultError.message}
              primaryButton={{
                title: doneLabel,
                onPress: goHome,
              }}
            />
          ) : null
        }
      />
      {!advanceScreenContentLoading && !advanceScreenContentErrorModel && (
        <ActionScreen
          visual="COFFEE_A_ANIMATED"
          titleText={pendingScreenContent?.title ?? ''}
          titleTextStyle="extra-large"
          bodyContent={
            <BodyBaseText numberOfLines={0}>
              {pendingScreenContent?.description ?? ''}
            </BodyBaseText>
          }
          primaryButton={{
            label: doneLabel,
            onPress: goHome,
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default Pending;
